import { useState, type FormEvent } from 'react'
import type { BehaviorDefinition, BehaviorDirection, CustomBehaviorInput } from '../index.js'
import type { CustomBasis } from '../index.js'
import { BEHAVIOR_KEY_PATTERN, keyFromLabel } from './behaviorDrafts.js'
import { useEndpointHints, EndpointHint } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { Plus, Save } from 'lucide-react'

const INPUT_CLASS = 'w-full rounded border border-marine/30 bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20'
const LABEL_CLASS = 'block text-[11px] font-bold text-ink-mid mb-1'

const DIRECTION_LABELS: Record<BehaviorDirection, string> = {
  positive: 'Counts for the worker',
  negative: 'Counts against the worker',
}

const BASIS_LABELS: Record<CustomBasis, string> = {
  per_period: 'Counted over the scoring period',
  per_shift: 'Counted per scheduled shift',
  per_period_minimum: 'Counted against a minimum per period',
}

/** A minimum is "at least this many", so it is only offered for a behavior that counts for the worker; the API refuses the other pairing. */
function basisAllowed(basis: CustomBasis, direction: BehaviorDirection): boolean {
  return basis !== 'per_period_minimum' || direction === 'positive'
}

interface FormState {
  label: string
  /** Null until the person edits the key themselves; until then it follows the label. */
  keyOverride: string | null
  description: string
  direction: BehaviorDirection
  basis: CustomBasis
}

const EMPTY_FORM: FormState = { label: '', keyOverride: null, description: '', direction: 'positive', basis: 'per_period' }

interface BehaviorFormProps {
  /** The behavior being edited, or null when adding one. A key cannot change once created. */
  editing: BehaviorDefinition | null
  isSaving: boolean
  error: string | null
  onSubmit: (input: CustomBehaviorInput) => void
  onCancel: () => void
}

function BehaviorForm({ editing, isSaving, error, onSubmit, onCancel }: BehaviorFormProps) {
  const [form, setForm] = useState<FormState>(
    editing ? { label: editing.label, keyOverride: editing.key, description: editing.description, direction: editing.direction, basis: editing.basis ?? 'per_period' } : EMPTY_FORM,
  )
  const key = form.keyOverride ?? keyFromLabel(form.label)
  const isKeyValid = BEHAVIOR_KEY_PATTERN.test(key)
  const canSubmit = form.label.trim() !== '' && isKeyValid && !isSaving
  // The API resets the behavior's anchors everywhere when either of these changes; say so before it happens.
  const measuresDifferently = editing !== null && (editing.direction !== form.direction || (editing.basis ?? 'per_period') !== form.basis)

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (canSubmit) {
      onSubmit({ key, label: form.label.trim(), description: form.description.trim(), direction: form.direction, basis: form.basis })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border border-marine/15 rounded bg-white p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={LABEL_CLASS} htmlFor="custom-behavior-label">
            Name
          </label>
          <input
            id="custom-behavior-label"
            className={INPUT_CLASS}
            value={form.label}
            maxLength={60}
            autoFocus
            onChange={(event) => setForm({ ...form, label: event.target.value })}
            placeholder="e.g. In-Service Completed"
          />
        </div>
        <div>
          <label className={LABEL_CLASS} htmlFor="custom-behavior-key">
            Event key
          </label>
          <input
            id="custom-behavior-key"
            className={`${INPUT_CLASS} font-mono ${editing ? 'opacity-60' : ''}`}
            value={key}
            disabled={editing !== null}
            onChange={(event) => setForm({ ...form, keyOverride: event.target.value })}
          />
          <p className={`text-[11px] mt-1 ${key !== '' && !isKeyValid ? 'text-coral font-bold' : 'text-ink-mid'}`}>
            {editing
              ? 'The key cannot change: partners already send it.'
              : '3 to 40 lowercase letters, digits or underscores, starting with a letter. Partners send this as behavior_key.'}
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className={LABEL_CLASS} htmlFor="custom-behavior-description">
            Description
          </label>
          <input
            id="custom-behavior-description"
            className={INPUT_CLASS}
            value={form.description}
            maxLength={300}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            placeholder="What a partner is reporting when it sends this event"
          />
        </div>
        <fieldset className="sm:col-span-2">
          <legend className={LABEL_CLASS}>When it happens, it</legend>
          <div className="flex flex-wrap gap-4">
            {(Object.keys(DIRECTION_LABELS) as BehaviorDirection[]).map((direction) => (
              <label key={direction} className="flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input
                  type="radio"
                  name="custom-behavior-direction"
                  checked={form.direction === direction}
                  onChange={() => setForm({ ...form, direction, basis: basisAllowed(form.basis, direction) ? form.basis : 'per_period' })}
                />
                {DIRECTION_LABELS[direction].replace('Counts', 'counts')}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="sm:col-span-2">
          <legend className={LABEL_CLASS}>When scored, it is</legend>
          <div className="flex flex-wrap gap-4">
            {(Object.keys(BASIS_LABELS) as CustomBasis[]).map((basis) => (
              <label key={basis} className={`flex items-center gap-2 text-sm cursor-pointer ${basisAllowed(basis, form.direction) ? 'text-ink' : 'text-ink-mid'}`}>
                <input type="radio" name="custom-behavior-basis" checked={form.basis === basis} disabled={!basisAllowed(basis, form.direction)} onChange={() => setForm({ ...form, basis })} />
                {BASIS_LABELS[basis].replace('Counted', 'counted')}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-ink-mid mt-1">
            {measuresDifferently
              ? 'Changing this resets the behavior’s 100-at and 0-at levels to their defaults in the master and every facility, since the old ones were on a different scale.'
              : 'Over the period: a count judged between the levels that earn 0 and 100, scaled to the period’s length. Per shift: “missed a handoff note”. Against a minimum: “attended the monthly in-service” - full marks from the minimum, nothing below it, whatever the period’s length; only for a behavior that counts for the worker.'}
          </p>
        </fieldset>
      </div>

      {error && (
        <p role="alert" className="text-[11px] font-bold text-coral">
          {error}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button icon={editing ? Save : Plus} type="submit" disabled={!canSubmit}>
          {isSaving ? 'Saving…' : editing ? 'Save behavior' : 'Add behavior'}
        </Button>
        <button type="button" disabled={isSaving} onClick={onCancel} className="text-xs font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
      </div>
    </form>
  )
}

interface CustomBehaviorsPanelProps {
  custom: readonly BehaviorDefinition[]
  isSaving: boolean
  error: string | null
  onCreate: (input: CustomBehaviorInput) => Promise<boolean>
  onUpdate: (key: string, changes: Omit<CustomBehaviorInput, 'key'>) => Promise<boolean>
  onDelete: (key: string) => Promise<boolean>
}

/**
 * The client's own behaviors: add one, rename it, or remove it. A new one
 * starts off with no weight - defining a behavior changes nothing until it
 * is switched on above.
 */
export function CustomBehaviorsPanel({ custom, isSaving, error, onCreate, onUpdate, onDelete }: CustomBehaviorsPanelProps) {
  // 'new' while adding, a key while editing that behavior, null when the form is closed.
  const [open, setOpen] = useState<string | null>(null)
  const showsEndpoints = useEndpointHints()
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)

  async function submit(input: CustomBehaviorInput): Promise<void> {
    const { key, ...changes } = input
    const saved = open === 'new' ? await onCreate(input) : await onUpdate(key, changes)
    if (saved) {
      setOpen(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-xs font-extrabold text-ink">Custom behaviors</div>
          <div className="text-[11px] text-ink-mid">
            Behaviors of your own, each reported by partners{showsEndpoints && <> with <EndpointHint>POST /events/behavior</EndpointHint></>}. New ones start switched off.
          </div>
        </div>
        {open === null && (
          <Button icon={Plus} variant="secondary" size="sm" className="shrink-0" onClick={() => setOpen('new')}>
            Add behavior
          </Button>
        )}
      </div>

      {open === 'new' && <BehaviorForm editing={null} isSaving={isSaving} error={error} onSubmit={(input) => void submit(input)} onCancel={() => setOpen(null)} />}

      {custom.length === 0 && open === null ? (
        <div className="border border-dashed border-marine/30 rounded p-4 text-xs text-ink-mid">No custom behaviors yet.</div>
      ) : (
        <ul className="space-y-2 mt-2">
          {custom.map((behavior) =>
            open === behavior.key ? (
              <li key={behavior.key}>
                <BehaviorForm editing={behavior} isSaving={isSaving} error={error} onSubmit={(input) => void submit(input)} onCancel={() => setOpen(null)} />
              </li>
            ) : (
              <li key={behavior.key} className="border border-marine/15 rounded bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-ink">
                    {behavior.label} <span className="font-mono font-normal text-[11px] text-ink-mid">{behavior.key}</span>
                  </div>
                  <div className="text-[11px] text-ink-mid">
                    {DIRECTION_LABELS[behavior.direction]} · {BASIS_LABELS[behavior.basis ?? 'per_period'].toLowerCase()}
                    {behavior.description && ` · ${behavior.description}`}
                  </div>
                </div>
                {confirmingDelete === behavior.key ? (
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-ink">Remove it everywhere? Events already reported are kept.</span>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => void onDelete(behavior.key).then(() => setConfirmingDelete(null))}
                      className="font-bold text-coral hover:text-coral/80 disabled:opacity-50"
                    >
                      Remove
                    </button>
                    <button type="button" disabled={isSaving} onClick={() => setConfirmingDelete(null)} className="font-bold text-ink-mid hover:text-ink">
                      Keep
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-[11px]">
                    <button type="button" onClick={() => setOpen(behavior.key)} className="font-bold text-marine hover:text-marine-dk">
                      Edit
                    </button>
                    <button type="button" onClick={() => setConfirmingDelete(behavior.key)} className="font-bold text-coral hover:text-coral/80">
                      Remove…
                    </button>
                  </div>
                )}
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  )
}
